// P:\0PJ\synforge\frontend\app\settings\data\page.tsx v2 2026.07.30 - Request Data Export and Delete Account buttons connected to real API

'use client';

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { authAPI } from '@/lib/api';
import { useLocale } from '@/contexts/LocaleContext';

export default function DataPage() {
  const { t } = useLocale();
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [exportSent, setExportSent] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleExport = async () => {
    setExporting(true);
    setExportError('');
    try {
      await authAPI.exportMyData();
      setExportSent(true);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    setDeleteError('');
    try {
      await authAPI.deleteMyAccount();
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteError('Failed to delete account. Please try again.');
      setDeleting(false);
    }
  };

  function Card({ title, children }: { title: string; children: React.ReactNode }) {
    return (
      <div className="space-y-5 pb-8 mb-8 border-b border-white/[0.05] last:border-0 last:mb-0 last:pb-0">
        <h3 className="text-base font-semibold text-white tracking-tight">{title}</h3>
        {children}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[640px] space-y-4">
      <Card title="Export Your Data">
        <p className="text-xs text-white/40">Your export will include meeting history, results, vault files, and account information.</p>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="bg-white/[0.05] hover:bg-white/[0.09] border border-white/10 text-white text-sm px-5 py-2.5 rounded-xl transition-colors disabled:opacity-40"
        >
          {exporting ? 'Preparing export...' : 'Request Data Export'}
        </button>
        {exportError && <p className="text-[11px] text-red-400">{exportError}</p>}
        {exportSent && <p className="text-[11px] text-green-400">Check your email for a download link and password. The link expires in 1 hour.</p>}
      </Card>
      <Card title="Delete Account">
        <div className="flex items-start gap-3 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-xs text-white/40">
            This action is <span className="text-red-400 font-medium">irreversible</span>. Type <span className="text-white font-mono">DELETE</span> to confirm.
          </p>
        </div>
        <input
          value={deleteConfirm}
          onChange={(e) => setDeleteConfirm(e.target.value)}
          placeholder="DELETE"
          className="w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-purple-500/60 transition-colors"
        />
        {deleteError && <p className="text-[11px] text-red-400">{deleteError}</p>}
        <button
          onClick={handleDelete}
          disabled={deleteConfirm !== 'DELETE' || deleting}
          className="bg-red-600 hover:bg-red-500 disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-xl transition-colors"
        >
          {deleting ? 'Deleting...' : t('common.deleteAccount')}
        </button>
      </Card>
    </div>
  );
}